import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Send, Loader2, User, RotateCcw, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CharacterChatPage = () => {
  const [searchParams] = useSearchParams();
  const [universes, setUniverses] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedUniverse, setSelectedUniverse] = useState("");
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const uniRes = await axios.get(`${API}/universes`);
        setUniverses(uniRes.data);
        const preUni = searchParams.get("universe");
        if (preUni) {
          const match = uniRes.data.find(u => u.slug === preUni);
          if (match) setSelectedUniverse(match.id);
        }
      } catch { /* silent */ }
    };
    fetchData();
  }, [searchParams]);

  useEffect(() => {
    if (!selectedUniverse) { setCharacters([]); return; }
    const fetchChars = async () => {
      try {
        const res = await axios.get(`${API}/characters?universe_id=${selectedUniverse}`);
        setCharacters(res.data);
        const preChar = searchParams.get("character");
        if (preChar) {
          const match = res.data.find(c => c.name === preChar || c.id === preChar);
          if (match) setSelectedCharacter(match);
        }
      } catch { /* silent */ }
    };
    fetchChars();
  }, [selectedUniverse, searchParams]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const getUniverseName = () => universes.find(u => u.id === selectedUniverse)?.name || "";

  const sendMessage = async (text) => {
    if (!text?.trim() || !selectedCharacter) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/character-chat`, {
        character_name: selectedCharacter.name,
        universe_name: getUniverseName(),
        message: text.trim(),
        session_id: sessionId,
        history: messages.slice(-6),
      });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.reply, character: selectedCharacter.name }]);
    } catch {
      toast.error("Character is unavailable right now");
      setMessages(prev => [...prev, { role: "assistant", content: "...", character: selectedCharacter.name }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); sendMessage(input); };

  const resetChat = () => { setMessages([]); setInput(""); };

  const selectCharacter = (char) => {
    setSelectedCharacter(char);
    setMessages([]);
    setInput("");
  };

  const starters = [
    `What's the most dangerous situation you've ever been in?`,
    `What do you think about the current state of ${getUniverseName()}?`,
    `Tell me about your closest ally.`,
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex flex-col">
      <SEOHead title={selectedCharacter ? `Chat with ${selectedCharacter.name}` : "Character Chat"} description="Talk to your favorite fictional characters powered by AI." />
      <Navbar />
      <main className="flex-1 pt-20 pb-8 px-4 max-w-4xl mx-auto w-full flex flex-col">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-purple-500/20 bg-purple-500/5 mb-3">
            <User className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-mono tracking-wider text-purple-500 uppercase">AI Character Chat</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-2">
            {selectedCharacter ? `Chat with ${selectedCharacter.name}` : "Character Chat"}
          </h1>
          <p className="text-slate-400 text-sm">Talk to your favorite fictional characters. They stay fully in character.</p>
        </div>

        {/* Selectors */}
        <div className="flex items-center gap-3 mb-4 justify-center flex-wrap">
          <Globe className="w-4 h-4 text-slate-500" />
          <Select value={selectedUniverse} onValueChange={(v) => { setSelectedUniverse(v); setSelectedCharacter(null); resetChat(); }}>
            <SelectTrigger className="w-48 bg-[var(--bg-surface)] border-white/10 text-white" data-testid="char-universe-select">
              <SelectValue placeholder="Select Universe" />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10">
              {universes.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {characters.length > 0 && (
            <Select value={selectedCharacter?.id || ""} onValueChange={(v) => selectCharacter(characters.find(c => c.id === v))}>
              <SelectTrigger className="w-56 bg-[var(--bg-surface)] border-white/10 text-white" data-testid="char-select">
                <SelectValue placeholder="Choose Character" />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-white/10 max-h-64">
                {characters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={resetChat} className="text-slate-500 hover:text-white" data-testid="char-reset">
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-h-0 bg-[var(--bg-surface)] rounded-xl border border-white/5 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="char-chat-area">
            {/* Empty — no character */}
            {!selectedCharacter && (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <User className="w-12 h-12 text-slate-600 mb-4" />
                <p className="text-slate-500">{selectedUniverse ? "Choose a character to start chatting" : "Select a universe and character above"}</p>
              </div>
            )}

            {/* Empty — character selected but no messages */}
            {selectedCharacter && messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-purple-500/30 mb-4 bg-[var(--bg-elevated)]">
                  {selectedCharacter.image ? (
                    <img src={selectedCharacter.image} alt={selectedCharacter.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><User className="w-8 h-8 text-slate-600" /></div>
                  )}
                </div>
                <p className="text-white font-['Outfit'] font-semibold text-lg">{selectedCharacter.name}</p>
                {selectedCharacter.species && <p className="text-purple-400 text-xs font-mono uppercase tracking-wider mt-1">{selectedCharacter.species}</p>}
                <p className="text-slate-500 text-sm mt-2 mb-5">Start a conversation</p>
                <div className="flex flex-col gap-2 w-full max-w-md">
                  {starters.map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)} className="text-left px-4 py-3 rounded-lg bg-white/[0.03] border border-white/5 text-sm text-slate-300 hover:bg-purple-500/10 hover:border-purple-500/20 hover:text-white transition-all" data-testid={`char-starter-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`char-msg-${i}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === "user"
                    ? "bg-purple-500/15 border border-purple-500/20 text-white"
                    : "bg-white/[0.04] border border-white/5 text-slate-300"
                }`}>
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <User className="w-3 h-3 text-purple-400" />
                      <span className="text-[10px] font-mono tracking-wider text-purple-400 uppercase">{msg.character}</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="char-loading">
                <div className="bg-white/[0.04] border border-white/5 rounded-2xl px-4 py-3 flex items-center gap-2">
                  <User className="w-3 h-3 text-purple-400" />
                  <span className="text-[10px] font-mono tracking-wider text-purple-400 uppercase">{selectedCharacter?.name}</span>
                  <Loader2 className="w-4 h-4 text-purple-400 animate-spin ml-2" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 sm:p-4 border-t border-white/5 bg-[var(--bg-base)]/50">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={selectedCharacter ? `Say something to ${selectedCharacter.name}...` : "Select a character first"}
                disabled={!selectedCharacter || loading}
                className="flex-1 bg-[var(--bg-elevated)] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-purple-500/30 transition-colors disabled:opacity-40"
                data-testid="char-input"
              />
              <Button type="submit" disabled={!input.trim() || !selectedCharacter || loading} className="bg-purple-500 hover:bg-purple-600 text-white rounded-xl px-4" data-testid="char-send">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CharacterChatPage;
